/**
 * Multi-Study Analytics Hook
 *
 * Handles all analytics tracking for the multi-study prescreening flow.
 * Separates analytics concerns from business logic.
 */

import { useCallback } from "react";
import { useAnalytics } from "../../../hooks/useAnalytics";
import useLandingPageIds from "../../../hooks/useLandingPageIds";
import { Study } from "../types";

interface UseMultiStudyAnalyticsProps {
  landingPageId: string;
}

/**
 * Hook for tracking multi-study analytics events
 */
export function useMultiStudyAnalytics({
  landingPageId,
}: UseMultiStudyAnalyticsProps) {
  const { track } = useAnalytics();
  const { variantId, masterId } = useLandingPageIds();

  /**
   * Track a multi-study event with standard properties
   */
  const trackEvent = useCallback(
    (eventName: string, properties: Record<string, unknown> = {}) => {
      track(`lp.multi_study.${eventName}`, {
        landing_page_id: landingPageId,
        variant_id: variantId || landingPageId,
        master_id: masterId || landingPageId,
        ...properties,
      });
    },
    [track, landingPageId, variantId, masterId]
  );

  return {
    /**
     * Track completion of common questions
     */
    trackCommonQuestionsCompleted: useCallback(
      (questionsAnswered: number) => {
        trackEvent("common_questions_completed", {
          questions_answered: questionsAnswered,
        });
      },
      [trackEvent]
    ),

    /**
     * Track location filtering results
     */
    trackLocationFiltered: useCallback(
      (zipcode: string, eligibleStudies: Study[]) => {
        trackEvent("location_filtered", {
          zipcode,
          total_studies: eligibleStudies.length,
          location_eligible_studies: eligibleStudies.length,
          eligible_study_ids: eligibleStudies.map((s) => s.id),
        });
      },
      [trackEvent]
    ),

    /**
     * Track when no studies match a location
     */
    trackNoLocationMatch: useCallback(
      (zipcode: string) => {
        trackEvent("no_location_match", {
          zipcode,
        });
      },
      [trackEvent]
    ),

    /**
     * Track when study prescreening starts
     */
    trackStudyPrescreeningStarted: useCallback(
      (study: Study, eligibleStudiesCount: number, isCascade: boolean = false) => {
        trackEvent("study_prescreening_started", {
          study_id: study.id,
          study_name: study.name,
          study_priority: study.priority,
          eligible_studies_count: eligibleStudiesCount,
          is_cascade: isCascade,
        });
      },
      [trackEvent]
    ),

    /**
     * Track successful study match
     */
    trackStudyMatched: useCallback(
      (study: Study, studiesChecked: number, totalEligible: number) => {
        trackEvent("study_matched", {
          study_id: study.id,
          study_name: study.name,
          study_priority: study.priority,
          studies_checked: studiesChecked,
          total_location_eligible: totalEligible,
        });
      },
      [trackEvent]
    ),

    /**
     * Track when a study disqualifies a user
     */
    trackStudyDisqualified: useCallback(
      (study: Study, studyIndex: number, totalEligible: number) => {
        trackEvent("study_disqualified", {
          study_id: study.id,
          study_name: study.name,
          study_priority: study.priority,
          study_index: studyIndex, // 0-based index in priority order
          total_location_eligible: totalEligible,
          will_cascade: studyIndex < totalEligible - 1,
        });
      },
      [trackEvent]
    ),

    /**
     * Track cascade to next study
     */
    trackStudyCascade: useCallback(
      (failedStudy: Study, nextStudy: Study, remainingStudies: number) => {
        trackEvent("study_cascade", {
          failed_study_id: failedStudy.id,
          failed_study_name: failedStudy.name,
          failed_study_priority: failedStudy.priority,
          next_study_id: nextStudy.id,
          next_study_name: nextStudy.name,
          next_study_priority: nextStudy.priority,
          remaining_studies: remainingStudies,
        });
      },
      [trackEvent]
    ),

    /**
     * Track when all studies fail
     */
    trackAllStudiesFailed: useCallback(
      (studiesChecked: Study[]) => {
        trackEvent("all_studies_failed", {
          location_eligible_studies: studiesChecked.length,
          studies_checked: studiesChecked.map((s) => s.id),
        });
      },
      [trackEvent]
    ),
  };
}
